import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuSliderOptions} from '@Components/contextual-menu/components/contextual-menu-slider-options';
import {LayoutTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {useGetKeyFromTableItem} from '@Hooks/poster-editor/useGetKeyFromTableItem';

const HORIZONTAL_SPACING_MIN = 0;
const HORIZONTAL_SPACING_MAX = 250;

const layoutsWithNonAdjustableXSpacing = [
  LayoutTypes.BAND_LAYOUT_LEFT_ALIGN,
  LayoutTypes.BAND_LAYOUT_RIGHT_ALIGN,
  LayoutTypes.BAND_LAYOUT_CENTER_ALIGN,
  LayoutTypes.OFFSET_SPORTS_LAYOUT,
];

export function TableHorizontalSpacing(): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const itemScaleX = useGetKeyFromActiveItem('scaleX');
  const itemLayoutStyle = useGetKeyFromTableItem('layoutStyle') as LayoutTypes;
  const itemXSpacing = useGetKeyFromTableItem('xSpacing');

  if (!activeItem) {
    throw new Error('Active item does not exist!');
  }

  if (!('xSpacing' in activeItem) || !('layoutStyle' in activeItem) || itemScaleX === undefined || itemXSpacing === undefined) {
    throw new Error('item doesnt have Xspacing or layoutStyle property');
  }

  if (layoutsWithNonAdjustableXSpacing.includes(itemLayoutStyle)) {
    return null;
  }

  const updateXSpacing = (xSpacing: number, undoable = true): void => {
    void activeItem.updateFromObject(
      {
        xSpacing,
      },
      {
        undoable,
      }
    );
  };

  const onChangeValue = (updatedValue: number): void => {
    updateXSpacing(updatedValue / itemScaleX, false);
  };

  const onChangeEnded = (updatedValue: number): void => {
    updateXSpacing(updatedValue / itemScaleX);
  };

  return (
    <ContextualMenuSliderOptions
      id="table-horizontal-spacing"
      value={Math.round(itemXSpacing * itemScaleX)}
      label={window.i18next.t('pmwjs_horizontal_spacing')}
      range={{
        min: HORIZONTAL_SPACING_MIN,
        max: HORIZONTAL_SPACING_MAX,
      }}
      transformDisplay={{
        min: HORIZONTAL_SPACING_MIN,
        max: HORIZONTAL_SPACING_MAX,
      }}
      onChange={onChangeValue}
      onChangeEnded={onChangeEnded}
    />
  );
}
